<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">POS Orders <span class="gl-pos-badge">Loyverse</span></h1>
        <div class="gl-breadcrumb">
          <router-link to="/">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span>POS Orders</span>
        </div>
      </div>
      <div class="gl-header-actions">
        <router-link to="/owner/loyverse/settings" class="gl-btn-ghost">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>
          Settings
        </router-link>
        <button class="gl-btn-refresh" @click="fetchOrders()" :class="{ 'gl-refreshing': loading }">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="gl-refresh-icon"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          Refresh
        </button>
      </div>
    </div>

    <!-- Stats strip / quick-filter chips -->
    <div class="gl-pos-stats">
      <button
        class="gl-pos-chip"
        :class="['gl-chip-new', { 'gl-chip-active': filters.status === 'new' }]"
        @click="quickFilter('new')"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
        <div>
          <div class="gl-chip-count">{{ stats.new }}</div>
          <div class="gl-chip-label">New Orders</div>
        </div>
        <div v-if="stats.new > 0" class="gl-chip-pulse"></div>
      </button>

      <button
        class="gl-pos-chip"
        :class="['gl-chip-viewed', { 'gl-chip-active': filters.status === 'viewed' }]"
        @click="quickFilter('viewed')"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
        <div>
          <div class="gl-chip-count">{{ stats.viewed }}</div>
          <div class="gl-chip-label">Viewed</div>
        </div>
      </button>

      <button
        class="gl-pos-chip"
        :class="['gl-chip-printed', { 'gl-chip-active': filters.status === 'printed' }]"
        @click="quickFilter('printed')"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        <div>
          <div class="gl-chip-count">{{ stats.printed }}</div>
          <div class="gl-chip-label">Printed</div>
        </div>
      </button>

      <button
        class="gl-pos-chip"
        :class="['gl-chip-done', { 'gl-chip-active': filters.status === 'completed' }]"
        @click="quickFilter('completed')"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="20 6 9 17 4 12"/></svg>
        <div>
          <div class="gl-chip-count">{{ stats.completed }}</div>
          <div class="gl-chip-label">Completed</div>
        </div>
      </button>
    </div>

    <!-- Filter bar -->
    <div class="gl-filter-bar">
      <div class="gl-select-wrap">
        <select class="gl-select" v-model="filters.status" @change="fetchOrders()">
          <option value="">All Status</option>
          <option value="new">New</option>
          <option value="viewed">Viewed</option>
          <option value="printed">Printed</option>
          <option value="completed">Completed</option>
        </select>
        <svg class="gl-select-chevron" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
      </div>

      <input type="date" class="gl-date-input" v-model="filters.date" @change="fetchOrders()">

      <button class="gl-ar-pill" :class="{ 'gl-ar-on': autoRefresh }" @click="autoRefresh = !autoRefresh">
        <span class="gl-ar-dot" :class="{ 'gl-ar-dot-live': autoRefresh }"></span>
        Auto-refresh (30s)
      </button>

      <button class="gl-btn-clear" @click="resetFilters">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        Clear
      </button>
    </div>

    <!-- Loading skeleton -->
    <div v-if="loading && !orders.length" class="gl-orders-grid">
      <div v-for="i in 6" :key="i" class="gl-skel-order-card">
        <div class="gl-skel-card-head"></div>
        <div style="padding:14px">
          <div class="gl-skel gl-skel-line" style="width:60%;margin-bottom:8px"></div>
          <div class="gl-skel gl-skel-line" style="width:80%;margin-bottom:6px"></div>
          <div class="gl-skel gl-skel-line" style="width:70%;margin-bottom:6px"></div>
          <div class="gl-skel gl-skel-line" style="width:40%;margin-top:12px"></div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="!orders.length" class="gl-empty-card">
      <div class="gl-empty-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
      </div>
      <div class="gl-empty-title">No orders found</div>
      <div class="gl-empty-sub">Orders from Loyverse POS will appear here</div>
    </div>

    <!-- Orders grid -->
    <div v-else class="gl-orders-grid">
      <div
        v-for="(order, idx) in orders" :key="order.id"
        class="gl-order-card"
        :class="[`gl-order-${order.status}`, { 'gl-order-latest': idx === 0 && pagination.current_page === 1 }]"
      >
        <!-- Card header -->
        <div class="gl-order-head">
          <div class="gl-order-num">{{ getDisplayOrderNumber(order) }}</div>
          <span v-if="idx === 0 && pagination.current_page === 1" class="gl-latest-pill">Latest</span>
          <span class="gl-status-pill" :class="`gl-status-${order.status}`">{{ order.status }}</span>
          <span class="gl-order-time" :title="order.created_at">{{ formatTime(order.created_at) }}</span>
        </div>

        <!-- Which POS store this came from -->
        <div class="gl-order-store">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l1-5h16l1 5"/><path d="M4 9v11h16V9"/><path d="M9 20v-6h6v6"/></svg>
          {{ order.store_label }}
          <span v-if="!order.is_mapped" class="gl-unmapped-tag" title="This POS store is not linked to a GetLokal outlet yet">unmapped</span>
        </div>

        <!-- Customer / table -->
        <div v-if="order.customer_name || order.table_number || order.dining_option" class="gl-order-meta">
          <span v-if="order.customer_name" class="gl-meta-item">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
            {{ order.customer_name }}
          </span>
          <span v-if="order.table_number" class="gl-meta-item">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="8" y2="21"/><line x1="16" y1="3" x2="14" y2="21"/></svg>
            Table {{ order.table_number }}
          </span>
          <span v-if="order.dining_option" class="gl-dining-chip">{{ formatDiningOption(order.dining_option) }}</span>
        </div>

        <!-- Items -->
        <div class="gl-order-items">
          <div v-for="(item, idx) in order.items" :key="idx" class="gl-order-item">
            <div class="gl-item-row">
              <span class="gl-item-qty">{{ item.quantity }}×</span>
              <span class="gl-item-name">{{ item.item_name }}<span v-if="item.variant_name" class="gl-item-variant"> · {{ item.variant_name }}</span></span>
              <span class="gl-item-price">RM {{ formatPrice(item.total) }}</span>
            </div>
            <div v-if="item.note" class="gl-item-note">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {{ item.note }}
            </div>
          </div>
        </div>

        <!-- Total -->
        <div class="gl-order-total">
          <span>Total</span>
          <span class="gl-total-amt">RM {{ formatPrice(order.total) }}</span>
        </div>

        <!-- Footer actions -->
        <div class="gl-order-footer">
          <button class="gl-btn-print" @click="printOrder(order)" :disabled="printingId === order.id">
            <span v-if="printingId === order.id" class="gl-spinner"></span>
            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print Label
          </button>
          <button v-if="order.status !== 'completed'" class="gl-btn-done" @click="markCompleted(order)" :disabled="completingId === order.id">
            <span v-if="completingId === order.id" class="gl-spinner gl-spinner-white"></span>
            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Done
          </button>
          <span v-else class="gl-completed-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Completed
          </span>
        </div>
      </div>
    </div>

    <!-- Result range: the tiles above count every order, this grid shows one page -->
    <div v-if="!loading && orders.length" class="gl-result-range">
      Showing <strong>{{ rangeStart }}–{{ rangeEnd }}</strong> of <strong>{{ pagination.total }}</strong> orders
      <span v-if="pagination.last_page > 1">· page {{ pagination.current_page }} of {{ pagination.last_page }}</span>
      <span class="gl-range-sorted">newest first</span>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.last_page > 1" class="gl-pagination">
      <button class="gl-page-btn" :disabled="pagination.current_page === 1" @click="fetchOrders(pagination.current_page - 1)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <button
        v-for="page in pagination.last_page" :key="page"
        class="gl-page-btn"
        :class="{ 'gl-page-active': page === pagination.current_page }"
        @click="fetchOrders(page)"
      >{{ page }}</button>
      <button class="gl-page-btn" :disabled="pagination.current_page === pagination.last_page" @click="fetchOrders(pagination.current_page + 1)">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
      </button>
    </div>

    <!-- Print Labels Modal -->
    <div v-if="printModalOpen" class="gl-modal-backdrop" @click.self="printModalOpen = false">
      <div class="gl-modal gl-modal-print">
        <div class="gl-modal-header">
          <div class="gl-modal-title-row">
            <h3 class="gl-modal-title">Print Labels</h3>
            <span v-if="expandedLabels.length" class="gl-label-count">{{ expandedLabels.length }} labels</span>
          </div>
          <button class="gl-modal-close" @click="printModalOpen = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div ref="printContent">
            <div v-for="(label, index) in expandedLabels" :key="index" class="gl-label-preview">
              <div class="gl-label-sticker">
                <div class="gl-label-head">
                  <span class="gl-label-order">{{ label.order_number }}</span>
                  <span class="gl-label-counter">{{ label.label_index }}/{{ label.label_total }}</span>
                </div>
                <div class="gl-label-item">
                  {{ label.item_name }}
                  <span v-if="label.variant" class="gl-label-variant">({{ label.variant }})</span>
                </div>
                <div v-if="label.modifiers && label.modifiers.length" class="gl-label-mods">
                  {{ label.modifiers.map(m => m.name || m).join(', ') }}
                </div>
                <div class="gl-label-foot">
                  <span>{{ label.date }} {{ label.time }}</span>
                  <span v-if="label.customer_name" class="gl-label-cust">{{ label.customer_name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-ghost" @click="printModalOpen = false">Close</button>
          <button class="gl-btn-done" @click="doPrint">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Print All ({{ expandedLabels.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition name="gl-toast">
      <div v-if="toastMsg" class="gl-toast" :class="toastType === 'error' ? 'gl-toast-error' : 'gl-toast-success'">
        <svg v-if="toastType !== 'error'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        {{ toastMsg }}
      </div>
    </transition>
  </div>
</template>

<script>
import { ownerLoyverseApi } from '@/services/ownerApi';

export default {
  name: 'LoyverseOrders',
  data() {
    return {
      loading: true,
      printingId: null,
      completingId: null,
      autoRefresh: true,
      refreshInterval: null,
      orders: [],
      printData: null,
      expandedLabels: [],
      printModalOpen: false,
      selectedOrder: null,
      toastMsg: null,
      toastType: 'success',
      toastTimer: null,
      stats: { new: 0, viewed: 0, printed: 0, completed: 0 },
      filters: { status: '', date: '' },
      pagination: { current_page: 1, last_page: 1, per_page: 20, total: 0 }
    };
  },
  computed: {
    rangeStart() {
      if (!this.pagination.total) return 0;
      return (this.pagination.current_page - 1) * this.pagination.per_page + 1;
    },
    rangeEnd() {
      return Math.min(this.pagination.current_page * this.pagination.per_page, this.pagination.total);
    }
  },
  watch: {
    autoRefresh(v) { v ? this.startAutoRefresh() : this.stopAutoRefresh(); }
  },
  mounted() {
    this.fetchOrders();
    if (this.autoRefresh) this.startAutoRefresh();
  },
  beforeUnmount() {
    this.stopAutoRefresh();
  },
  methods: {
    quickFilter(status) {
      this.filters.status = this.filters.status === status ? '' : status;
      this.fetchOrders();
    },
    startAutoRefresh() {
      this.stopAutoRefresh();
      this.refreshInterval = setInterval(() => this.fetchOrders(this.pagination.current_page, true), 30000);
    },
    stopAutoRefresh() {
      if (this.refreshInterval) { clearInterval(this.refreshInterval); this.refreshInterval = null; }
    },
    async fetchOrders(page = 1, silent = false) {
      try {
        if (!silent) this.loading = true;
        const params = { page, per_page: this.pagination.per_page, today: !this.filters.date, ...this.filters };
        Object.keys(params).forEach(k => { if (params[k] === '' || params[k] === null) delete params[k]; });
        const response = await ownerLoyverseApi.getOrders(params);
        const payload = response.data.data;
        this.orders = payload.data || payload;
        // Laravel's paginator returns current_page/total at the top level of the
        // payload; there is no nested `meta` object, so reading one left the
        // pagination state empty and the range line showing "0-0 of 0".
        const m = payload.meta || payload;
        if (m && m.current_page) {
          this.pagination = {
            current_page: m.current_page,
            last_page: m.last_page,
            per_page: m.per_page,
            total: m.total
          };
        }
        this.calculateStats();
      } catch (e) {
        console.error('Failed to fetch orders:', e);
      } finally {
        if (!silent) this.loading = false;
      }
    },
    // Counts come from the server across the full result set. Deriving them from
    // this.orders only ever counted the current page, so the tiles capped at
    // per_page (20) no matter how many receipts existed.
    async calculateStats() {
      try {
        const response = await ownerLoyverseApi.getStats({
          today: !this.filters.date,
          ...(this.filters.date ? { date: this.filters.date } : {})
        });
        const s = response.data.data;
        this.stats = {
          new: s.new,
          viewed: s.viewed,
          printed: s.printed,
          completed: s.completed
        };
      } catch (e) {
        console.error('Failed to fetch stats:', e);
      }
    },
    resetFilters() {
      this.filters = { status: '', date: '' };
      this.fetchOrders();
    },
    getDisplayOrderNumber(order) {
      if (order.receipt_number) return '#' + order.receipt_number.slice(-4);
      return '#' + String(order.id).padStart(4, '0');
    },
    formatTime(ds) {
      if (!ds) return '';
      // Seconds included: POS receipts often arrive within the same minute, and
      // minute-only timestamps made the ordering look arbitrary.
      return new Date(ds).toLocaleTimeString('en-MY', {
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      });
    },
    formatPrice(n) { return parseFloat(n || 0).toFixed(2); },
    formatDiningOption(o) { if (!o) return ''; return o.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()); },
    expandItemsForLabels(pd) {
      if (!pd || !pd.items) return [];
      const labels = [];
      pd.items.forEach(item => {
        for (let i = 0; i < (item.quantity || 1); i++) {
          labels.push({ item_name: item.name, variant: item.variant || null, modifiers: item.modifiers || [], notes: item.notes || null, order_number: pd.order_number, customer_name: pd.customer_name || null, date: pd.date, time: pd.created_at });
        }
      });
      labels.forEach((l, i) => { l.label_index = i + 1; l.label_total = labels.length; });
      return labels;
    },
    async printOrder(order) {
      try {
        this.printingId = order.id;
        const r = await ownerLoyverseApi.getPrintData(order.id);
        this.printData = r.data.data;
        this.selectedOrder = order;
        this.expandedLabels = this.expandItemsForLabels(this.printData);
        this.printModalOpen = true;
      } catch (e) {
        this.showToast('Failed to load print data', 'error');
      } finally {
        this.printingId = null;
      }
    },
    async doPrint() {
      const labels = this.expandedLabels;
      if (!labels.length) return;
      let labelsHtml = '';
      labels.forEach((label, index) => {
        const isLast = index === labels.length - 1;
        const modsHtml = (label.modifiers?.length) ? `<div class="modifiers">${label.modifiers.map(m => m.name || m).join(', ')}</div>` : '';
        const custHtml = label.customer_name ? `<span class="customer">${label.customer_name}</span>` : '';
        const varHtml = label.variant ? ` <span class="variant">(${label.variant})</span>` : '';
        labelsHtml += `<div class="label" ${!isLast ? 'style="page-break-after:always"' : ''}><div class="label-row header-row"><span class="order-num">${label.order_number}</span><span class="counter">${label.label_index}/${label.label_total}</span></div><div class="item-name">${label.item_name}${varHtml}</div>${modsHtml}<div class="label-row footer-row"><span>${label.date} ${label.time}</span>${custHtml}</div></div>`;
      });
      const w = window.open('', '_blank', 'width=300,height=250');
      w.document.write(`<html><head><title>Order Labels</title><style>@page{size:50mm 30mm;margin:1mm}*{box-sizing:border-box;margin:0;padding:0}body{font-family:Arial,sans-serif;font-size:7px;line-height:1.2}.label{width:48mm;height:28mm;padding:1.5mm;display:flex;flex-direction:column;justify-content:space-between;overflow:hidden}.label-row{display:flex;justify-content:space-between;align-items:center}.header-row{border-bottom:.5px solid #000;padding-bottom:1mm;margin-bottom:1mm}.order-num{font-weight:bold;font-size:9px}.counter{font-size:8px;font-weight:bold}.item-name{font-weight:bold;font-size:10px;flex-grow:1;display:flex;align-items:center}.variant{font-weight:normal;font-size:8px;color:#333;margin-left:2px}.modifiers{font-size:7px;color:#444;font-style:italic}.footer-row{border-top:.5px solid #000;padding-top:1mm;margin-top:1mm;font-size:6px;color:#333}.customer{font-weight:bold}@media screen{body{padding:10px}.label{border:1px dashed #ccc;margin-bottom:5px}}</style></head><body>${labelsHtml}</body></html>`);
      w.document.close(); w.focus();
      setTimeout(() => { w.print(); w.close(); }, 250);
      if (this.selectedOrder) {
        await ownerLoyverseApi.markAsPrinted(this.selectedOrder.id);
        this.fetchOrders(this.pagination.current_page, true);
      }
      this.printModalOpen = false;
    },
    async markCompleted(order) {
      try {
        this.completingId = order.id;
        await ownerLoyverseApi.markAsCompleted(order.id);
        const idx = this.orders.findIndex(o => o.id === order.id);
        if (idx !== -1) this.orders[idx].status = 'completed';
        this.calculateStats();
        this.showToast('Order marked as completed');
      } catch (e) {
        this.showToast('Failed to update order status', 'error');
      } finally {
        this.completingId = null;
      }
    },
    showToast(msg, type = 'success') {
      this.toastMsg = msg; this.toastType = type;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toastMsg = null; }, 3200);
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-page { padding: 28px 32px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; display: flex; align-items: center; gap: 10px; }
.gl-pos-badge { font-size: 11px; font-weight: 600; padding: 3px 9px; background: #f0f8f5; color: #900C27; border: 1px solid #c8e6de; border-radius: 20px; letter-spacing: .2px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; }
.gl-breadcrumb a { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb a:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }
.gl-header-actions { display: flex; gap: 10px; margin-top: 2px; }

.gl-btn-ghost { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; font-size: 13px; font-weight: 500; background: #fff; color: #4a6360; border: 1.5px solid #d4dedd; border-radius: 8px; cursor: pointer; text-decoration: none; transition: border-color .15s; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }
.gl-btn-refresh { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; font-size: 13px; font-weight: 500; background: #900C27; color: #fff; border: none; border-radius: 8px; cursor: pointer; transition: background .15s; }
.gl-btn-refresh:hover { background: #1a5248; }
.gl-refresh-icon { transition: transform .5s ease; }
.gl-refreshing .gl-refresh-icon { animation: gl-spin .8s linear infinite; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Stats chips ── */
.gl-pos-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
.gl-pos-chip {
  display: flex; align-items: center; gap: 12px; padding: 16px 18px;
  background: #fff; border: 1.5px solid #eaedec; border-radius: 12px;
  cursor: pointer; text-align: left; transition: border-color .15s, box-shadow .15s;
  position: relative; overflow: hidden;
}
.gl-pos-chip:hover { border-color: #b5cdc8; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
.gl-chip-count { font-size: 26px; font-weight: 700; color: #1a2e2b; letter-spacing: -0.5px; line-height: 1; }
.gl-chip-label { font-size: 11.5px; color: #8a9e9a; font-weight: 500; margin-top: 2px; }

.gl-chip-new svg { color: #900C27; }
.gl-chip-viewed svg { color: #3b82f6; }
.gl-chip-printed svg { color: #d97706; }
.gl-chip-done svg { color: #16a34a; }

.gl-chip-active.gl-chip-new    { border-color: #900C27; background: #f0f8f5; box-shadow: 0 0 0 3px rgba(144,12,39,.1); }
.gl-chip-active.gl-chip-viewed { border-color: #3b82f6; background: #eff6ff; box-shadow: 0 0 0 3px rgba(59,130,246,.1); }
.gl-chip-active.gl-chip-printed { border-color: #f59e0b; background: #fffbeb; box-shadow: 0 0 0 3px rgba(245,158,11,.1); }
.gl-chip-active.gl-chip-done   { border-color: #16a34a; background: #f0fdf4; box-shadow: 0 0 0 3px rgba(22,163,74,.1); }

.gl-chip-pulse {
  position: absolute; top: 12px; right: 12px;
  width: 8px; height: 8px; border-radius: 50%; background: #900C27;
  animation: gl-pulse 1.5s infinite;
}
@keyframes gl-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(144,12,39,.4); }
  50% { box-shadow: 0 0 0 6px rgba(144,12,39,0); }
}

/* ── Filter bar ── */
.gl-filter-bar { display: flex; align-items: center; gap: 12px; background: transparent; border: none; padding: 0; margin-bottom: 24px; flex-wrap: wrap; }
.gl-select-wrap { position: relative; }
.gl-select { appearance: none; padding: 8px 30px 8px 12px; font-size: 13px; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px; outline: none; cursor: pointer; font-family: inherit; }
.gl-select:focus { border-color: #900C27; }
.gl-select-chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #8a9e9a; }
.gl-date-input { padding: 8px 12px; font-size: 13px; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px; outline: none; font-family: inherit; }
.gl-date-input:focus { border-color: #900C27; }

.gl-ar-pill { display: inline-flex; align-items: center; gap: 7px; padding: 8px 14px; font-size: 12.5px; font-weight: 500; color: #6b7f7b; background: #f0f5f4; border: 1.5px solid #dde8e6; border-radius: 20px; cursor: pointer; transition: all .15s; }
.gl-ar-on { background: #f0f8f5; border-color: #900C27; color: #900C27; }
.gl-ar-dot { width: 7px; height: 7px; border-radius: 50%; background: #c0ccc9; flex-shrink: 0; }
.gl-ar-dot-live { background: #900C27; animation: gl-pulse 1.5s infinite; }

.gl-btn-clear { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; font-size: 12.5px; color: #8a9e9a; background: none; border: 1.5px solid #e0e9e7; border-radius: 8px; cursor: pointer; margin-left: auto; transition: border-color .15s; }
.gl-btn-clear:hover { border-color: #b0c4c0; color: #5a7470; }

/* ── Orders grid ── */
.gl-orders-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 16px; }

.gl-order-card {
  background: #fff; border: 1.5px solid #eaedec; border-radius: 12px;
  display: flex; flex-direction: column; overflow: hidden;
  border-left-width: 4px; transition: box-shadow .15s;
}
.gl-order-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.08); }
.gl-order-new     { border-left-color: #900C27; }
.gl-order-viewed  { border-left-color: #3b82f6; }
.gl-order-printed { border-left-color: #f59e0b; }
.gl-order-completed { border-left-color: #16a34a; opacity: .85; }

.gl-order-head { display: flex; align-items: center; gap: 8px; padding: 13px 15px 10px; border-bottom: 1px solid #f0f3f2; }
.gl-order-num { font-size: 16px; font-weight: 800; color: #1a2e2b; flex-shrink: 0; }
.gl-status-pill { font-size: 10.5px; font-weight: 700; padding: 2px 8px; border-radius: 20px; text-transform: capitalize; }
.gl-status-new      { background: #e0f0ec; color: #1a5248; }
.gl-status-viewed   { background: #dbeafe; color: #1d4ed8; }
.gl-status-printed  { background: #fef3c7; color: #92400e; }
.gl-status-completed { background: #dcfce7; color: #166534; }
.gl-order-time { margin-left: auto; font-size: 11.5px; color: #8a9e9a; font-variant-numeric: tabular-nums; }

/* Newest order on page 1 */
.gl-order-latest { box-shadow: 0 0 0 2px rgba(144,12,39,.25); }
.gl-latest-pill {
  font-size: 9.5px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase;
  padding: 2px 7px; border-radius: 20px; background: #900C27; color: #fff;
}

/* Which POS store the receipt came from */
.gl-order-store {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 14px; font-size: 11.5px; color: #6b7280;
  background: #fafbfb; border-bottom: 1px solid #f1f3f2;
}
.gl-unmapped-tag {
  font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .3px;
  padding: 1px 6px; border-radius: 4px; background: #fff4e5; color: #b45309; cursor: help;
}

/* Reconciles the stat tiles (all orders) with the grid (one page) */
.gl-result-range {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  margin-top: 16px; font-size: 12px; color: #6b7280;
}
.gl-result-range strong { color: #111318; font-weight: 700; }
.gl-range-sorted {
  margin-left: auto; font-size: 11px; color: #9ca3af;
  padding: 2px 9px; border-radius: 20px; background: #f3f4f6;
}

.gl-order-meta { display: flex; align-items: center; gap: 10px; padding: 8px 15px; background: #f9fbfa; border-bottom: 1px solid #f0f3f2; flex-wrap: wrap; }
.gl-meta-item { display: flex; align-items: center; gap: 4px; font-size: 12px; color: #5a7470; }
.gl-dining-chip { font-size: 11px; font-weight: 600; padding: 2px 8px; background: #eef3f2; color: #4a6360; border-radius: 20px; }

.gl-order-items { padding: 12px 15px; flex: 1; overflow-y: auto; max-height: 200px; display: flex; flex-direction: column; gap: 8px; }
.gl-order-item { }
.gl-item-row { display: flex; align-items: baseline; gap: 6px; }
.gl-item-qty { font-size: 12px; font-weight: 700; color: #900C27; flex-shrink: 0; }
.gl-item-name { font-size: 13px; color: #1a2e2b; font-weight: 500; flex: 1; }
.gl-item-variant { font-size: 11.5px; color: #8a9e9a; font-weight: 400; }
.gl-item-price { font-size: 12.5px; color: #4a6360; font-weight: 600; flex-shrink: 0; }
.gl-item-note { display: flex; align-items: center; gap: 4px; font-size: 11px; color: #a0b5b0; font-style: italic; margin-top: 2px; padding-left: 18px; }

.gl-order-total { display: flex; align-items: center; justify-content: space-between; padding: 10px 15px; border-top: 1px solid #f0f3f2; border-bottom: 1px solid #f0f3f2; }
.gl-order-total span:first-child { font-size: 12px; color: #8a9e9a; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; }
.gl-total-amt { font-size: 16px; font-weight: 800; color: #1a2e2b; }

.gl-order-footer { display: flex; align-items: center; gap: 8px; padding: 11px 15px; }
.gl-btn-print { display: inline-flex; align-items: center; gap: 6px; padding: 7px 13px; font-size: 12.5px; font-weight: 500; color: #900C27; background: #f0f8f5; border: 1.5px solid #c8e6de; border-radius: 7px; cursor: pointer; transition: background .15s; }
.gl-btn-print:hover:not(:disabled) { background: #e0f0ec; }
.gl-btn-print:disabled { opacity: .5; cursor: not-allowed; }
.gl-btn-done { display: inline-flex; align-items: center; gap: 6px; padding: 7px 13px; font-size: 12.5px; font-weight: 600; color: #fff; background: #900C27; border: none; border-radius: 7px; cursor: pointer; transition: background .15s; margin-left: auto; }
.gl-btn-done:hover:not(:disabled) { background: #1a5248; }
.gl-btn-done:disabled { opacity: .55; cursor: not-allowed; }
.gl-completed-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; font-weight: 600; color: #16a34a; margin-left: auto; }

/* ── Empty / skeleton ── */
.gl-empty-card { background: #fff; border: 1px solid #eaedec; border-radius: 12px; text-align: center; padding: 64px 24px; }
.gl-empty-icon { width: 52px; height: 52px; border-radius: 14px; background: #eef5f3; color: #900C27; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.gl-empty-title { font-size: 15px; font-weight: 600; color: #1a2e2b; margin-bottom: 5px; }
.gl-empty-sub { font-size: 13px; color: #8a9e9a; }

.gl-skel-order-card { background: #fff; border: 1.5px solid #eaedec; border-radius: 12px; overflow: hidden; }
.gl-skel-card-head { height: 48px; background: #f0f3f2; }
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-line { height: 13px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Pagination ── */
.gl-pagination { display: flex; justify-content: center; align-items: center; gap: 6px; margin-top: 28px; }
.gl-page-btn { display: flex; align-items: center; justify-content: center; min-width: 36px; height: 36px; padding: 0 10px; font-size: 13px; color: #4a6360; background: #fff; border: 1.5px solid #e0e9e7; border-radius: 8px; cursor: pointer; transition: border-color .15s; }
.gl-page-btn:hover:not(:disabled) { border-color: #900C27; color: #900C27; }
.gl-page-btn:disabled { opacity: .4; cursor: not-allowed; }
.gl-page-active { background: #900C27; color: #fff !important; border-color: #900C27 !important; font-weight: 700; }

/* ── Modal ── */
.gl-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; }
.gl-modal { background: #fff; border-radius: 16px; width: 100%; box-shadow: 0 24px 60px rgba(0,0,0,.18); animation: gl-modal-in .2s ease; display: flex; flex-direction: column; max-height: 90vh; }
.gl-modal-print { max-width: 480px; }
@keyframes gl-modal-in { from { opacity: 0; transform: scale(.96) translateY(8px); } to { opacity: 1; } }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eef0ef; }
.gl-modal-title-row { display: flex; align-items: center; gap: 10px; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #1a2e2b; margin: 0; }
.gl-label-count { font-size: 12px; font-weight: 600; padding: 2px 8px; background: #e0f0ec; color: #1a5248; border-radius: 20px; }
.gl-modal-close { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; background: #f8faf9; border: none; color: #6b7f7b; cursor: pointer; transition: background .12s; }
.gl-modal-close:hover { background: #eef0ef; }
.gl-modal-body { overflow-y: auto; padding: 16px 24px; flex: 1; }
.gl-modal-footer { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px; border-top: 1px solid #eef0ef; }

/* ── Label sticker preview ── */
.gl-label-preview { display: flex; justify-content: center; margin-bottom: 12px; }
.gl-label-sticker { width: 220px; border: 1px dashed #c8d5d2; border-radius: 6px; padding: 9px 11px; font-size: 11px; background: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.06); }
.gl-label-head { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px dashed #dde5e4; padding-bottom: 5px; margin-bottom: 5px; }
.gl-label-order { font-weight: 800; font-size: 15px; color: #1a2e2b; }
.gl-label-counter { font-size: 11px; font-weight: 600; color: #8a9e9a; }
.gl-label-item { font-weight: 700; font-size: 13px; color: #1a2e2b; margin-bottom: 3px; }
.gl-label-variant { font-weight: 400; font-size: 11px; color: #6b7f7b; margin-left: 3px; }
.gl-label-mods { font-size: 10px; color: #8a9e9a; font-style: italic; margin-bottom: 3px; }
.gl-label-foot { display: flex; justify-content: space-between; font-size: 9.5px; color: #8a9e9a; border-top: 1px dashed #dde5e4; padding-top: 4px; margin-top: 4px; }
.gl-label-cust { font-weight: 700; color: #4a6360; }

/* ── Spinner ── */
.gl-spinner { display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(144,12,39,.25); border-top-color: #900C27; border-radius: 50%; animation: gl-spin .65s linear infinite; }
.gl-spinner-white { border-color: rgba(255,255,255,.3); border-top-color: #fff; }

/* ── Toast ── */
.gl-toast { position: fixed; bottom: 28px; right: 28px; z-index: 2000; display: flex; align-items: center; gap: 9px; padding: 12px 18px; border-radius: 10px; font-size: 13.5px; font-weight: 500; box-shadow: 0 8px 24px rgba(0,0,0,.14); }
.gl-toast-success { background: #1a5248; color: #fff; }
.gl-toast-error { background: #dc2626; color: #fff; }
.gl-toast-enter-active, .gl-toast-leave-active { transition: all .3s ease; }
.gl-toast-enter-from, .gl-toast-leave-to { opacity: 0; transform: translateY(12px) scale(.95); }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }

  /* Header: title on top, buttons below right-aligned */
  .gl-page-header { flex-wrap: wrap; gap: 10px; margin-bottom: 16px; }
  .gl-header-actions { width: 100%; justify-content: flex-end; }

  /* Buttons: icon-only on mobile */
  .gl-btn-ghost { padding: 9px 12px; }
  .gl-btn-ghost span, .gl-btn-refresh span { display: none; }
  .gl-btn-refresh { padding: 9px 12px; }

  /* Hide button text — SVG stays */
  .gl-btn-ghost svg ~ *, .gl-btn-refresh svg ~ * { display: none; }

  /* Stats chips: 2×2 grid */
  .gl-pos-stats { grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 16px; }
  .gl-pos-chip { padding: 13px 14px; gap: 10px; }
  .gl-chip-count { font-size: 22px; }
  .gl-chip-label { font-size: 11px; }

  /* Filter bar: transparent, tighter */
  .gl-filter-bar {
    background: transparent;
    border: none;
    padding: 0;
    gap: 8px;
    margin-bottom: 16px;
  }
  .gl-select, .gl-date-input { width: 100%; box-sizing: border-box; }
  .gl-select-wrap { flex: 1; }
  .gl-ar-pill { flex: 1; justify-content: center; }
  .gl-btn-clear { margin-left: 0; flex: 1; justify-content: center; }

  /* Orders grid: single column */
  .gl-orders-grid { grid-template-columns: 1fr; }

  /* Modal */
  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; max-height: calc(100vh - 84px); }
  .gl-modal-header { padding: 16px 16px 14px; }
  .gl-modal-body { padding: 14px 16px; }
  .gl-modal-footer { padding: 14px 16px; }
  .gl-btn-ghost, .gl-btn-done { flex: 1; justify-content: center; padding: 9px 12px; }

  /* Toast */
  .gl-toast { bottom: 16px; right: 16px; left: 16px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #5a7470; }
body.dark-only .gl-pos-badge { background: #1c2f2b; border-color: #2a3d3a; color: #4caf9a; }
body.dark-only .gl-btn-ghost { background: #1e2d2b; border-color: #2a3d3a; color: #8ab5ad; }
body.dark-only .gl-btn-refresh { background: #900C27; }
body.dark-only .gl-pos-chip { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-chip-count { color: #e0eded; }
body.dark-only .gl-filter-bar { background: transparent; border: none; }
body.dark-only .gl-select, body.dark-only .gl-date-input { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-ar-pill { background: #16211f; border-color: #2a3d3a; color: #7aada5; }
body.dark-only .gl-ar-on { background: #1c2f2b; border-color: #4caf9a; color: #4caf9a; }
body.dark-only .gl-order-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-order-store { background: rgba(255,255,255,.03); border-bottom-color: #2a3d3a; color: rgba(255,255,255,.5); }
body.dark-only .gl-unmapped-tag { background: rgba(180,83,9,.2); color: #fbbf24; }
body.dark-only .gl-result-range { color: rgba(255,255,255,.45); }
body.dark-only .gl-result-range strong { color: rgba(255,255,255,.87); }
body.dark-only .gl-range-sorted { background: rgba(255,255,255,.06); color: rgba(255,255,255,.4); }
body.dark-only .gl-order-head { border-bottom-color: #243432; }
body.dark-only .gl-order-num { color: #e0eded; }
body.dark-only .gl-order-meta { background: #192b28; border-bottom-color: #243432; }
body.dark-only .gl-item-name { color: #e0eded; }
body.dark-only .gl-order-total { border-color: #243432; }
body.dark-only .gl-total-amt { color: #e0eded; }
body.dark-only .gl-order-footer { border-top-color: #243432; }
body.dark-only .gl-btn-print { background: #1c2f2b; border-color: #2a3d3a; color: #4caf9a; }
body.dark-only .gl-empty-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-empty-title { color: #e0eded; }
body.dark-only .gl-skel-order-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-skel-card-head { background: #243432; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-page-btn { background: #1e2d2b; border-color: #2a3d3a; color: #7aada5; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-close { background: #16211f; color: #7aada5; }
body.dark-only .gl-modal-footer { border-top-color: #2a3d3a; }
body.dark-only .gl-label-sticker { background: #16211f; border-color: #2a3d3a; }
body.dark-only .gl-label-order { color: #e0eded; }
body.dark-only .gl-label-item { color: #e0eded; }
body.dark-only .gl-label-head { border-bottom-color: #2a3d3a; }
body.dark-only .gl-label-foot { border-top-color: #2a3d3a; }
</style>
